import { useQueryClient } from "@tanstack/react-query";

/**
 * Every read model that a stock movement moves. Receipts, transfers, corrections and stocktakes all
 * write the same append-only ledger (US-02.6), and each of these queries is a different view of it:
 * the ledger itself, the stocktake reports, the lot rows and their weights, the aggregated weight on
 * a coffee, the warehouse overview and the below-minimum alerts.
 *
 * <p>They are listed in one place because a mutation that forgets one of them leaves the screen
 * showing a number that is no longer true — the queries stay fresh for 30 s (see {@code main.tsx}),
 * so the stale view survives a tab switch.
 */
const STOCK_QUERY_PATHS = [
  "/api/v1/inventory/movements",
  "/api/v1/inventory/stocktakes",
  "/api/v1/inventory/lots",
  "/api/v1/inventory/lots/{id}",
  "/api/v1/inventory/lots/by-coffee/{coffeeId}",
  "/api/v1/inventory/coffees",
  "/api/v1/inventory/coffees/{id}",
  "/api/v1/inventory/overview",
  "/api/v1/inventory/coffees/alerts/below-par",
] as const;

/** Refreshes everything that reads the stock ledger or a projection of it. */
export function useInvalidateStockViews() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(
      STOCK_QUERY_PATHS.map((path) => queryClient.invalidateQueries({ queryKey: ["get", path] })),
    );
}
